import React, {useEffect, useState, useRef} from 'react';
import {get} from 'lodash';
import propTypes from 'prop-types';
import styled from '@emotion/styled';
import parse from 'html-react-parser';
import { Select, MenuItem, InputLabel, FormControl, ButtonBase } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {theme} from '../../../themes/default-theme';
import {ReactSVG} from 'react-svg';
import chevronDown from '../../../assets/icons/chevron-down.svg';
import mobileCheck from '../../../util/mobileCheck';
import Router from 'next/router';

export const dropdownTheme = createTheme({
  ...theme,
})

const liteTheme = createTheme({
  ...theme,
})

const v2Theme = createTheme({
  ...theme,
})

dropdownTheme.components = {
  ...theme.components,
  // Complete: {
  //   styleOverrides: {
  //     'fieldset': {
  //       background: 'red'
  //     }
  //   }
  // },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        "&.MuiInputLabel-outlined": {
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          paddingLeft: 19,
          fontSize: 14,
          '&.MuiInputLabel-shrink': {
            transform: 'translateY(-50%)'
          },
          [theme.breakpoints.up('md')]: {
            fontSize: 16
          }
        }
      },
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        "&.Mui-focused": {
          color: theme.palette.darkGrey,
        },
        '&.MuiFormLabel-filled': {
          display: 'none'
        }
      }
    }
  },
  MuiList: {
    styleOverrides: {
      root: {
        "&.MuiList-padding": {
          padding: 0,
        }
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        "&.MuiPaper-elevation8": {
          boxShadow: 'none',
          marginTop: 20,
          backgroundColor: 'white',
          border: `1px solid ${theme.palette.middleGrey}`,
        }
      }
    }
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        minHeight: '20px',
        lineHeight: '20px',

        "&.MuiSelect-select:focus": {
          backgroundColor: 'transparent'
        }
      },
      select: {
        [theme.breakpoints.up('md')]: {
          padding: '11px 19px 9px'
        }
      }
      // select: {
      //   paddingRight: 40,
      // },
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '16px',
        padding: '12.5px 19px',
        paddingTop: '12.5px',
        paddingBottom: '12.5px',
        "&:not(:last-child)": {
          borderBottom: `1px solid ${theme.palette.middleGrey}`,
        },
        "&.Mui-disabled": {
          opacity: 0.5
        },
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: theme.palette.vividBlue,
          color: 'white'
        }
      }
    }
  }
}

liteTheme.components = {
  ...dropdownTheme.components,
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
        border: 0,
        color: theme.palette.white,
      },
      notchedOutline: {
        border: 0
      },
      input: {
        padding: '5px 14px'
      }
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        color: theme.palette.white,
        "&.Mui-focused": {
          color: theme.palette.white,
        },
        '&.MuiFormLabel-filled': {
          display: 'none'
        }
      },
      filled: {
        display: 'none !important',
        opacity: 0
      }
    }
  }
}

v2Theme.components = {
  ...dropdownTheme.components,
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: 'white',
        border: `1px solid ${theme.palette.middleGrey}`,
        borderRadius: 24,
        '&:hover': {
          borderColor: theme.palette.darkGrey,
        },
        '&.Mui-focused': {
          // borderColor: theme.palette.vividBlue,
          backgroundColor: '#f3f4f7'
        }
      },
      notchedOutline: {
        border: 0
      },
      input: {
        padding: '10px 14px'
      }
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        color: theme.palette.darkGrey,
        "&.Mui-focused": {
          color: theme.palette.vividBlue,
        },
        '&.MuiFormLabel-filled': {
          display: 'none'
        }
      }
    }
  }
}

const ChevronIcon = styled(ButtonBase, {
  shouldForwardProp: (prop) => !['$variant', '$active'].includes(prop),
})`
  /* Single class selector to override MUI emotion styles */
  &[data-styled-chevron], &.styled-chevron {
    pointer-events: none;
    width: 24px;
    height: 24px;
    position: absolute;
    top: 7px;
    right: 10px;
    border-radius: 24px;
    transition: transform 300ms ease-in-out;
    z-index: 1;
    min-width: 24px;
    max-width: 24px;
    min-height: 24px;
    max-height: 24px;
    padding: 0;
    margin: 0;
    border: none;
    outline: none;
    background: transparent;
    box-sizing: border-box;
    
    /* Default background for regular theme */
    ${props => props.$variant !== 'lite' && props.$variant !== 'v2' && `
      background-color: ${theme.palette.vividBlue};
    `}

    /* Lite theme styles */
    ${props => props.$variant === 'lite' && `
      top: 4px;
      background-color: transparent;
    `}

    /* V2 theme styles */
    ${props => props.$variant === 'v2' && `
      background-color: white;
      
      && svg g,
      && svg path {
        stroke: #758094;
      }
    `}

    /* Medium breakpoint adjustments */
    ${theme.breakpoints.up('md')} {
      top: 10px;

      ${props => props.$variant === 'lite' && `
        top: 5px;
      `}
    }

    /* Active state rotation */
    ${props => props.$active === 1 && `
      && {
        transform: rotate(180deg);
      }
    `}

    /* SVG styling with enhanced specificity */
    && svg {
      // width: 100%;
      // height: 100%;
      // display: block;
      // box-sizing: border-box;
      
      /* Default white stroke for regular theme */
      [stroke-width] {
        stroke: white;
      }

    }
    
    /* Override MUI Select icon classes with higher specificity */
    &&&.MuiSelect-iconOpen,
    &&&.MuiSelect-icon {
      right: 10px;
      top: 7px;
      pointer-events: none;
      
      ${theme.breakpoints.up('md')} {
        top: 10px;
      }
    }
  }
`

const dropdownDefaultPropOnChange = () => {};
const dropdownDefaultPropOnUpdate = () => {};

const Dropdown = ({
  label = 'Select',
  options,
  onChange = dropdownDefaultPropOnChange,
  onUpdate = dropdownDefaultPropOnUpdate,
  alwaysNative = false,
  neverNative = false,
  defaultValue = undefined,
  value = undefined,
  formVal = undefined,
  error = false,
  showComplete = false,
  disabled = false,
  link = false,
  theme = 'regular',
  open = false,
  disableScroll = true
}) => {

  const [isNative, setNative] = useState(true);
  const [isOpen, setOpen] = useState(open);

  useEffect(() => {
    setOpen(open);
  }, [open])

  const handleChange = (value) => {
    if (link) {
      Router.push(value);
      return;
    }
    const item = options.find(item => item.value.toString() === value.toString());
    onChange(item)
    onUpdate(item) // for react hook forms
  }

  useEffect(() => {
    if (neverNative) {
      setNative(false);
    } else {
      const native = alwaysNative ? true : mobileCheck();
      setNative(native)
    }
  },[])


  const disableOnScroll = (e) => {

    if (document.body.classList.contains('overflow-hidden')) {
      e.preventDefault();
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isOpen])

  useEffect(() => {
    if (disableScroll) {
      window.addEventListener("wheel",  (e) => disableOnScroll(e), {passive: false});
      return window.removeEventListener("wheel", disableOnScroll);
    }
  }, [])

  const val = typeof(value) === 'object' ? get(value, 'value', undefined) : value
  
  const dropdownRef = useRef(null);

  return (
    <ThemeProvider theme={theme === 'lite' ? liteTheme : theme === 'v2' ? v2Theme : dropdownTheme}>
      <FormControl ref={dropdownRef} variant="outlined" fullWidth classes={{ root: showComplete && !error ? dropdownTheme.Complete : ''}}>
        {!isNative && !formVal &&
        <InputLabel 
          disableAnimation={true}
          focused={false}
          shrink={false}
          disabled={disabled}
          >{label}</InputLabel>
        }
        <Select
          onChange={(e) => {handleChange(e.target.value)}}
          native={isNative}
          value={formVal ? formVal.value : val || ''}
          defaultValue={defaultValue || undefined}
          error={error}
          disabled={disabled}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          IconComponent={() => (
            <ChevronIcon $variant={theme} $active={isOpen ? 1 : 0} data-styled-chevron>
              <ReactSVG src={chevronDown} />
            </ChevronIcon>
          )}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center"
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "center"
            },
            getContentAnchorEl: null,
            disableScrollLock: true,
          }}
        >
          {isNative && !defaultValue && label && <option value="">{label}</option> }
          {options.map(item => (
            isNative ? 
              <option key={item.id || item.value} value={item.value}>{parse(item.text)}</option>
              :
              <MenuItem key={item.id || item.value} value={item.value}>{parse(item.text)}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </ThemeProvider>
  )
}

Dropdown.propTypes = {
  label: propTypes.string,
  options: propTypes.array.isRequired,
  onChange: propTypes.func,
  onUpdate: propTypes.func,
  alwaysNative: propTypes.bool,
  formVal: propTypes.object,
  defaultValue: propTypes.string,
  value: propTypes.any,
  error: propTypes.bool,
  showComplete: propTypes.bool,
  disabled: propTypes.bool,
  link:  propTypes.bool,
  neverNative: propTypes.bool,
  theme: propTypes.oneOf(['regular', 'lite', 'v2']),
  open: propTypes.bool,
  disableScroll: propTypes.bool
}

export default Dropdown
